import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, ArrowLeft, Sparkles, Palette, Upload, Type,
  CheckCircle, Star, Gift, Package, Users, Shirt,
  Instagram, MessageCircle, Image, Wand2,
  ChevronRight, Play, Pause
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import Layout from '../components/layout/Layout';
import { BRAND, PRODUCT_OPTIONS } from '../data/mock';

const WHATSAPP = '918889616972';
const INSTAGRAM_USER = 'aura_printiqo';

const SelfCustomPage = () => {
  const [selectedType, setSelectedType] = useState('anime-transform');
  const [currentStep, setCurrentStep] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);

  const [selfFormData, setSelfFormData] = useState({
    name: '',
    phone: '',
    instagramHandle: '',
    product: '',
    size: '',
    description: ''
  });

  const handleSelfFormChange = (field, value) => {
    setSelfFormData(prev => ({ ...prev, [field]: value }));
  };

  const customizationTypes = [
    {
      id: 'anime-transform',
      title: 'AI Anime Portrait',
      subtitle: 'Neural Art Transformation',
      description: 'Your likeness, reimagined through the lens of anime aesthetics. Our AI captures your essence and translates it into stunning Japanese animation style artwork.',
      icon: Sparkles,
      startingPrice: '₹999',
      features: ['High-resolution output', '2 revision rounds', 'Multiple style options'],
      turnaround: '24-48 hours',
      color: 'from-violet-500 to-purple-600'
    },
    {
      id: 'creative-illustration',
      title: 'Creative Illustration',
      subtitle: 'Bespoke Digital Artistry',
      description: 'Commission a one-of-a-kind illustration crafted by our artists. From surreal compositions to minimalist portraits—your vision, elevated.',
      icon: Palette,
      startingPrice: '₹1,499',
      features: ['Artist collaboration', 'Unlimited concepts', 'Source file included'],
      turnaround: '3-5 days',
      color: 'from-amber-500 to-orange-600'
    },
    {
      id: 'own-design',
      title: 'Your Design',
      subtitle: 'Print-Ready Production',
      description: 'Already have artwork? Upload your design and we\'ll handle the production. Premium DTG printing on gallery-quality apparel.',
      icon: Upload,
      startingPrice: '₹799',
      features: ['Print consultation', 'Color matching', 'Test print option'],
      turnaround: '2-3 days',
      color: 'from-emerald-500 to-teal-600'
    },
    {
      id: 'typography-streetwear',
      title: 'Typography & Streetwear',
      subtitle: 'Urban Design Language',
      description: 'Bold statements, minimalist aesthetics. Custom typography, streetwear graphics, and contemporary design that speaks volumes.',
      icon: Type,
      startingPrice: '₹1,299',
      features: ['Font curation', 'Layout options', 'Brand guidelines'],
      turnaround: '2-4 days',
      color: 'from-slate-600 to-zinc-800'
    }
  ];

  const transformationSteps = [
    {
      step: 1,
      title: 'Your Photo',
      subtitle: 'The Starting Point',
      description: 'Share your vision with us. Upload a high-quality photo or describe your concept.',
      image: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=800',
      tips: ['Well-lit, front-facing photos work best', 'Minimum 1080px resolution', 'Neutral background preferred', 'Clear facial features visible']
    },
    {
      step: 2,
      title: 'Custom Artwork',
      subtitle: 'The Transformation',
      description: 'Our AI and artists work their magic, transforming your input into wearable art.',
      image: 'https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=800',
      tips: ['AI-powered style transfer', 'Artist refinement & enhancement', 'Preview before production', 'Revision rounds included']
    },
    {
      step: 3,
      title: 'Final Product',
      subtitle: 'The Masterpiece',
      description: 'Premium printing on gallery-quality apparel. Your exclusive, limited-edition piece.',
      image: 'https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?w=800',
      tips: ['300 DPI print resolution', 'Premium 180-320 GSM fabric', 'Colorfast DTG printing', 'Quality inspected']
    }
  ];

  const upsellOptions = [
    { icon: Shirt, title: 'Oversized Drop', description: 'Street-ready silhouette with extended fit', price: '+₹200', tag: 'Popular' },
    { icon: Star, title: 'Limited Edition', description: 'Numbered artwork with certificate', price: '+₹500', tag: 'Exclusive' },
    { icon: Gift, title: 'Gift Packaging', description: 'Premium box with tissue & card', price: '+₹150', tag: 'Gift Ready' },
    { icon: Users, title: 'Bulk Order', description: '5+ pieces with special pricing', price: 'Save 15%', tag: 'Best Value' }
  ];

  React.useEffect(() => {
    if (!isPlaying) return;
    const interval = setInterval(() => {
      setCurrentStep((prev) => (prev + 1) % 3);
    }, 3000);
    return () => clearInterval(interval);
  }, [isPlaying]);

  const selectedTypeData = customizationTypes.find(t => t.id === selectedType);

  const buildSelfMessage = () =>
    `Hi AuraPrintiqo! 👋\n\n*Self / Custom Order*\n━━━━━━━━━━━━━━━━\n*Name:* ${selfFormData.name}\n*Phone:* ${selfFormData.phone}\n*Instagram:* ${selfFormData.instagramHandle || 'Not provided'}\n\n*Order Details:*\n• Type: ${selectedTypeData?.title || selectedType}\n• Product: ${selfFormData.product || 'Not selected'}\n• Size: ${selfFormData.size || 'Not selected'}\n\n*My Vision:*\n${selfFormData.description || 'Will share details'}\n\nI'll share my reference photos after this message!`;

  const handleSelfWhatsApp = () => {
    window.open(
      `https://wa.me/${WHATSAPP}?text=${encodeURIComponent(buildSelfMessage())}`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  const handleSelfInstagram = () => {
    const msg = buildSelfMessage()
      .replace(/\n/g, '%0A')
      .replace(/ /g, '%20')
      .replace(/\*/g, '')
      .replace(/━/g, '-');
    window.open(`https://ig.me/m/${INSTAGRAM_USER}?text=${msg}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <Layout>
      {/* Hero */}
      <section className="relative min-h-[80vh] flex items-center bg-black text-white overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-black via-zinc-900 to-black" />
          <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-violet-600/20 rounded-full blur-[120px] animate-pulse" />
          <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-amber-600/20 rounded-full blur-[100px] animate-pulse" style={{ animationDelay: '1s' }} />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <Link to="/collections" className="inline-flex items-center gap-2 text-zinc-400 hover:text-white transition-colors mb-8 text-sm">
                <ArrowLeft className="w-4 h-4" />
                Back to Collections
              </Link>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-sm font-medium text-zinc-300 mb-6">
                <Sparkles className="w-4 h-4 text-violet-400" />
                Limited Edition of One
              </div>
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold leading-[1.1] mb-6">
                Self /<br />
                <span className="bg-gradient-to-r from-violet-400 via-pink-400 to-amber-400 bg-clip-text text-transparent">
                  Custom
                </span>
              </h1>
              <p className="text-xl text-zinc-400 leading-relaxed mb-8 max-w-lg">
                Your vision, our craft. From AI-powered transformations to bespoke illustrations—
                every piece is a unique expression of you.
              </p>
              <div className="flex flex-wrap gap-4 mb-10">
                <div className="px-4 py-2 bg-white/5 rounded-lg border border-white/10">
                  <span className="text-2xl font-bold text-white">₹999</span>
                  <span className="text-zinc-500 ml-2">starting</span>
                </div>
                <div className="px-4 py-2 bg-white/5 rounded-lg border border-white/10 flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-400" />
                  <span className="text-zinc-300">Preview before print</span>
                </div>
              </div>
              <a href="#order-form">
                <Button size="lg" className="bg-white text-black hover:bg-zinc-200 px-8 text-lg font-semibold">
                  Start Creating
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </a>
            </div>

            <div className="relative">
              <div className="relative aspect-square max-w-md mx-auto">
                <div className="absolute -top-4 left-0 right-0 flex justify-center gap-2 z-20">
                  {transformationSteps.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => { setCurrentStep(index); setIsPlaying(false); }}
                      className={`h-1 rounded-full transition-all duration-500 ${index === currentStep ? 'w-8 bg-white' : 'w-2 bg-white/30'}`}
                    />
                  ))}
                </div>
                <div className="relative w-full h-full rounded-3xl overflow-hidden">
                  {transformationSteps.map((step, index) => (
                    <div key={index} className={`absolute inset-0 transition-all duration-700 ${index === currentStep ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}>
                      <img src={step.image} alt={step.title} className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                    </div>
                  ))}
                  <div className="absolute bottom-6 left-6 right-6 z-10">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="w-8 h-8 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center text-sm font-bold">
                        {transformationSteps[currentStep].step}
                      </span>
                      <span className="text-sm text-zinc-400">{transformationSteps[currentStep].subtitle}</span>
                    </div>
                    <h3 className="text-2xl font-bold text-white">{transformationSteps[currentStep].title}</h3>
                  </div>
                  <button
                    onClick={() => setIsPlaying(!isPlaying)}
                    className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center hover:bg-white/20 transition-colors z-10"
                  >
                    {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                  </button>
                </div>
                <div className="absolute -right-4 top-1/4 bg-white/10 backdrop-blur-md rounded-xl p-3 border border-white/10">
                  <Wand2 className="w-5 h-5 text-violet-400" />
                </div>
                <div className="absolute -left-4 bottom-1/4 bg-white/10 backdrop-blur-md rounded-xl p-3 border border-white/10">
                  <Image className="w-5 h-5 text-amber-400" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Customization Types */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-zinc-100 rounded-full text-sm font-medium text-zinc-600 mb-4">Choose Your Path</span>
            <h2 className="text-4xl sm:text-5xl font-bold text-zinc-900 mb-4">Four Ways to Create</h2>
            <p className="text-xl text-zinc-500 max-w-2xl mx-auto">Select your customization style. Each path leads to a unique, wearable masterpiece.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {customizationTypes.map((type) => {
              const IconComponent = type.icon;
              const isSelected = selectedType === type.id;
              return (
                <button
                  key={type.id}
                  onClick={() => setSelectedType(type.id)}
                  className={`text-left p-6 rounded-2xl border-2 transition-all duration-300 ${isSelected ? 'border-zinc-900 bg-zinc-900 text-white shadow-2xl scale-[1.02]' : 'border-zinc-200 bg-white hover:border-zinc-300 hover:shadow-lg'}`}
                >
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${isSelected ? 'bg-white/20' : 'bg-zinc-100'}`}>
                    <IconComponent className={`w-6 h-6 ${isSelected ? 'text-white' : 'text-zinc-600'}`} />
                  </div>
                  <h3 className={`text-lg font-bold mb-1 ${isSelected ? 'text-white' : 'text-zinc-900'}`}>{type.title}</h3>
                  <p className="text-xs font-medium mb-3 text-zinc-400">{type.subtitle}</p>
                  <p className={`text-sm mb-4 leading-relaxed ${isSelected ? 'text-zinc-300' : 'text-zinc-500'}`}>{type.description}</p>
                  <div className="flex items-center justify-between">
                    <span className={`text-lg font-bold ${isSelected ? 'text-white' : 'text-zinc-900'}`}>{type.startingPrice}</span>
                    <span className="text-xs text-zinc-400">{type.turnaround}</span>
                  </div>
                </button>
              );
            })}
          </div>
          {selectedTypeData && (
            <div className="mt-12 p-8 bg-zinc-50 rounded-3xl">
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <h3 className="text-2xl font-bold text-zinc-900">{selectedTypeData.title}</h3>
                <span className="px-3 py-1 bg-zinc-900 text-white text-xs font-medium rounded-full">{selectedTypeData.turnaround}</span>
              </div>
              <p className="text-zinc-600 mb-6 max-w-2xl">{selectedTypeData.description}</p>
              <div className="flex flex-wrap gap-3">
                {selectedTypeData.features.map((feature, index) => (
                  <div key={index} className="flex items-center gap-2 px-4 py-2 bg-white rounded-full border border-zinc-200">
                    <CheckCircle className="w-4 h-4 text-emerald-500" />
                    <span className="text-sm text-zinc-700">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Transformation Workflow */}
      <section className="py-20 bg-zinc-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-white/10 rounded-full text-sm font-medium text-zinc-300 mb-4">The Process</span>
            <h2 className="text-4xl sm:text-5xl font-bold mb-4">Three Steps to Wearable Art</h2>
            <p className="text-xl text-zinc-400 max-w-2xl mx-auto">A seamless journey from your vision to a premium, gallery-quality print.</p>
          </div>
          <div className="grid lg:grid-cols-3 gap-8">
            {transformationSteps.map((step, index) => (
              <div key={index} className="relative">
                {index < 2 && <div className="hidden lg:block absolute top-1/2 -right-4 w-8 border-t-2 border-dashed border-zinc-700" />}
                <Card className="bg-zinc-800/50 border-zinc-700 overflow-hidden h-full">
                  <div className="relative h-64">
                    <img src={step.image} alt={step.title} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-zinc-900 via-zinc-900/50 to-transparent" />
                    <div className="absolute top-4 left-4">
                      <span className="w-10 h-10 rounded-full bg-white text-zinc-900 font-bold text-lg flex items-center justify-center">{step.step}</span>
                    </div>
                  </div>
                  <CardContent className="p-6">
                    <p className="text-sm text-zinc-500 mb-1">{step.subtitle}</p>
                    <h3 className="text-2xl font-bold text-white mb-3">{step.title}</h3>
                    <p className="text-zinc-400 mb-4">{step.description}</p>
                    <ul className="space-y-2">
                      {step.tips.map((tip, tipIndex) => (
                        <li key={tipIndex} className="flex items-start gap-2 text-sm text-zinc-300">
                          <ChevronRight className="w-4 h-4 text-zinc-500 flex-shrink-0 mt-0.5" />
                          {tip}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Image Guidelines */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="inline-block px-4 py-2 bg-violet-100 rounded-full text-sm font-medium text-violet-600 mb-4">Photo Guidelines</span>
              <h2 className="text-4xl font-bold text-zinc-900 mb-6">For Best Results</h2>
              <p className="text-lg text-zinc-500 mb-8">The quality of your input directly affects the output. Follow these guidelines for stunning results.</p>
              <div className="space-y-6">
                {[
                  { title: 'Resolution & Quality', items: ['Minimum 1080 x 1080 pixels', 'Sharp, in-focus image', 'Well-lit with even lighting', 'No heavy filters or edits'] },
                  { title: 'Framing & Composition', items: ['Face clearly visible (for portraits)', 'Front-facing or 3/4 angle', 'Avoid extreme close-ups', 'Leave space around subject'] },
                  { title: 'Background & Environment', items: ['Simple, uncluttered background', 'Solid colors work best', 'Avoid busy patterns', 'Natural lighting preferred'] }
                ].map((section, index) => (
                  <div key={index} className="p-6 bg-zinc-50 rounded-2xl">
                    <h4 className="font-semibold text-zinc-900 mb-3">{section.title}</h4>
                    <ul className="space-y-2">
                      {section.items.map((item, itemIndex) => (
                        <li key={itemIndex} className="flex items-center gap-2 text-sm text-zinc-600">
                          <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-4">
                  <div className="bg-emerald-50 rounded-2xl p-4 border-2 border-emerald-200">
                    <div className="aspect-square rounded-xl bg-zinc-200 mb-3 overflow-hidden">
                      <img src="https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=400" alt="Good example" className="w-full h-full object-cover" />
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-emerald-500" />
                      <span className="text-sm font-medium text-emerald-700">Perfect</span>
                    </div>
                    <p className="text-xs text-emerald-600 mt-1">Clear, well-lit, front-facing</p>
                  </div>
                  <div className="bg-zinc-100 rounded-2xl p-4">
                    <p className="text-xs text-zinc-500 uppercase tracking-wider mb-2">Print Specs</p>
                    <p className="text-sm text-zinc-700"><strong>Resolution:</strong> 300 DPI</p>
                    <p className="text-sm text-zinc-700"><strong>Format:</strong> PNG/JPEG</p>
                    <p className="text-sm text-zinc-700"><strong>Color:</strong> sRGB</p>
                  </div>
                </div>
                <div className="space-y-4 mt-8">
                  <div className="bg-red-50 rounded-2xl p-4 border-2 border-red-200">
                    <div className="aspect-square rounded-xl bg-zinc-300 mb-3 flex items-center justify-center">
                      <div className="text-center text-zinc-400">
                        <Image className="w-12 h-12 mx-auto mb-2 opacity-50" />
                        <span className="text-xs">Blurry/Dark</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="w-5 h-5 rounded-full bg-red-500 flex items-center justify-center text-white text-xs">✕</span>
                      <span className="text-sm font-medium text-red-700">Avoid</span>
                    </div>
                    <p className="text-xs text-red-600 mt-1">Low quality affects output</p>
                  </div>
                  <div className="bg-amber-50 rounded-2xl p-4 border border-amber-200">
                    <p className="text-sm font-medium text-amber-800 mb-1">Pro Tip</p>
                    <p className="text-xs text-amber-700">Natural daylight produces the best photos. Avoid direct sunlight which creates harsh shadows.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Upsells */}
      <section className="py-20 bg-zinc-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-2 bg-amber-100 rounded-full text-sm font-medium text-amber-700 mb-4">Elevate Your Order</span>
            <h2 className="text-3xl font-bold text-zinc-900 mb-4">Premium Add-Ons</h2>
            <p className="text-zinc-500 max-w-xl mx-auto">Make your piece even more special with these exclusive upgrades.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {upsellOptions.map((option, index) => (
              <Card key={index} className="border-0 shadow-soft hover:shadow-soft-lg transition-all duration-300 hover-lift overflow-hidden">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-12 h-12 rounded-xl bg-zinc-100 flex items-center justify-center">
                      <option.icon className="w-6 h-6 text-zinc-600" />
                    </div>
                    <span className="px-2 py-1 bg-zinc-900 text-white text-xs font-medium rounded">{option.tag}</span>
                  </div>
                  <h3 className="text-lg font-bold text-zinc-900 mb-1">{option.title}</h3>
                  <p className="text-sm text-zinc-500 mb-4">{option.description}</p>
                  <p className="text-lg font-bold text-violet-600">{option.price}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Order Form */}
      <section id="order-form" className="py-20 bg-white scroll-mt-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-2 bg-violet-100 rounded-full text-sm font-medium text-violet-600 mb-4">Start Your Order</span>
            <h2 className="text-4xl font-bold text-zinc-900 mb-4">Create Your Piece</h2>
            <p className="text-zinc-500 max-w-xl mx-auto">Fill out the form and choose WhatsApp or Instagram to send your order directly to us.</p>
          </div>

          <Card className="border-0 shadow-soft-lg overflow-hidden">
            <CardContent className="p-8">
              <form className="space-y-6">
                <div>
                  <Label htmlFor="sc-name" className="text-zinc-700 font-medium">Your Name *</Label>
                  <Input
                    id="sc-name"
                    placeholder="Enter your full name"
                    className="mt-2"
                    value={selfFormData.name}
                    onChange={(e) => handleSelfFormChange('name', e.target.value)}
                    required
                  />
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="sc-phone" className="text-zinc-700 font-medium">WhatsApp Number *</Label>
                    <Input
                      id="sc-phone"
                      placeholder="+91 XXXXX XXXXX"
                      className="mt-2"
                      value={selfFormData.phone}
                      onChange={(e) => handleSelfFormChange('phone', e.target.value)}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="sc-instagram" className="text-zinc-700 font-medium">Instagram Handle</Label>
                    <Input
                      id="sc-instagram"
                      placeholder="@username"
                      className="mt-2"
                      value={selfFormData.instagramHandle}
                      onChange={(e) => handleSelfFormChange('instagramHandle', e.target.value)}
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-zinc-700 font-medium mb-3 block">Customization Type *</Label>
                  <RadioGroup value={selectedType} onValueChange={setSelectedType} className="grid sm:grid-cols-2 gap-3">
                    {customizationTypes.map((type) => (
                      <div key={type.id} className="flex items-center space-x-3">
                        <RadioGroupItem value={type.id} id={`form-${type.id}`} />
                        <Label htmlFor={`form-${type.id}`} className="text-sm text-zinc-600 cursor-pointer">
                          {type.title} <span className="text-zinc-400">({type.startingPrice})</span>
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-zinc-700 font-medium">Product *</Label>
                    <Select onValueChange={(value) => handleSelfFormChange('product', value)}>
                      <SelectTrigger className="mt-2">
                        <SelectValue placeholder="Select product" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Premium T-Shirt">Premium T-Shirt (₹799)</SelectItem>
                        <SelectItem value="Oversized Tee">Oversized Tee (₹999)</SelectItem>
                        <SelectItem value="Premium Hoodie">Premium Hoodie (₹1,499)</SelectItem>
                        <SelectItem value="Sweatshirt">Sweatshirt (₹1,299)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-zinc-700 font-medium">Size *</Label>
                    <Select onValueChange={(value) => handleSelfFormChange('size', value)}>
                      <SelectTrigger className="mt-2">
                        <SelectValue placeholder="Select size" />
                      </SelectTrigger>
                      <SelectContent>
                        {PRODUCT_OPTIONS.tshirt.sizes.map((size) => (
                          <SelectItem key={size} value={size}>{size}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="sc-description" className="text-zinc-700 font-medium">Describe Your Vision *</Label>
                  <Textarea
                    id="sc-description"
                    placeholder="Tell us about your idea, share reference images, or describe the style you're looking for..."
                    className="mt-2"
                    rows={4}
                    value={selfFormData.description}
                    onChange={(e) => handleSelfFormChange('description', e.target.value)}
                    required
                  />
                </div>

                <div className="p-4 bg-zinc-50 rounded-xl border border-zinc-200">
                  <div className="flex items-start gap-3">
                    <Upload className="w-5 h-5 text-zinc-500 mt-0.5" />
                    <div>
                      <p className="font-medium text-zinc-700 text-sm">Photo Upload</p>
                      <p className="text-xs text-zinc-500 mt-1">
                        After submitting, share your photos via WhatsApp or Instagram DM. We'll send you the upload link.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="pt-4 space-y-4">
                  <p className="text-sm text-zinc-500 text-center font-medium">
                    Choose how to send your order:
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button
                      type="button"
                      onClick={handleSelfWhatsApp}
                      disabled={!selfFormData.name || !selfFormData.phone}
                      className="flex-1 bg-green-500 hover:bg-green-600 text-white py-6 text-base"
                    >
                      <MessageCircle className="w-5 h-5 mr-2" />
                      Send via WhatsApp
                    </Button>
                    <Button
                      type="button"
                      onClick={handleSelfInstagram}
                      disabled={!selfFormData.name || !selfFormData.phone}
                      className="flex-1 bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 hover:opacity-90 text-white py-6 text-base"
                    >
                      <Instagram className="w-5 h-5 mr-2" />
                      Send via Instagram DM
                    </Button>
                  </div>
                  <p className="text-xs text-zinc-400 text-center">
                    * Name and WhatsApp number are required. Your details will be pre-filled in the message.
                  </p>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-zinc-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">Ready to Wear Your Story?</h2>
          <p className="text-zinc-400 text-lg mb-8 max-w-2xl mx-auto">
            Every piece we create is a limited edition of one. Your vision, our craft, wearable art.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="#order-form">
              <Button size="lg" className="w-full sm:w-auto bg-white text-zinc-900 hover:bg-zinc-200 px-8">
                Start Creating
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </a>
            <Link to="/size-guide">
              <Button size="lg" variant="outline" className="w-full sm:w-auto border-2 border-zinc-600 text-white hover:bg-zinc-800 px-8">
                Size Guide
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default SelfCustomPage;
